import type { DeviceStorage } from '@linktr.ee/event-lib/client/context/ClientContextFactory';

/**
 * Mobile context parameters injected by the cerberus mobile app
 * via the Descope FlowView native hook (window.__MOBILE_APP_CONTEXT__).
 */
export interface MobileContextParams {
  deviceId?: string;
  osName?: string;
  osVersion?: string;
  appVersion?: string;
  deviceModel?: string;
  deviceType?: string;
  /** Last email or username used to log in from the mobile app. */
  lastLoginIdentifier?: string;
}

/**
 * Reads mobile context from window.__MOBILE_APP_CONTEXT__, which is injected
 * by the Descope FlowView native hook when running inside the mobile app WebView.
 *
 * @returns MobileContextParams or null if not a mobile app session
 */
export function getMobileContextParams(): MobileContextParams | null {
  const context = (
    window as unknown as { __MOBILE_APP_CONTEXT__?: MobileContextParams }
  ).__MOBILE_APP_CONTEXT__;

  if (!context) {
    return null;
  }

  return context;
}

/**
 * DeviceStorage implementation for the WebView context.
 * Reads mobile context injected by the cerberus native app and returns
 * it as a JSON string compatible with event-lib's AppContext.
 *
 * This allows MobileClientEventPublisher to enrich events with
 * native mobile device context when running inside a WebView.
 */
export class WebViewDeviceStorage implements DeviceStorage {
  private contextData: string;

  constructor(params: MobileContextParams) {
    this.contextData = JSON.stringify({
      deviceId: params.deviceId,
      deviceType: params.deviceType,
      deviceModel: params.deviceModel,
      deviceVendor: params.osName,
      osName: params.osName,
      osVersion: params.osVersion,
      appVersionName: params.appVersion,
      language:
        typeof navigator !== 'undefined' ? navigator.language || 'en' : 'en',
    });
  }

  getMobileContextData(): string {
    return this.contextData;
  }
}
